// Client-side CSV export from tables — no BI tooling (architecture.md §12).
//
// An export is the table, in a spreadsheet: the same columns in the same order, each cell
// holding the text the column's `render` puts on screen, under a short header block that
// records which report it is, who took it, when, and under which filters — so a file that
// has been mailed around for a week still says what it is.
import { isValidElement, type ReactNode } from "react";
import dayjs from "dayjs";
import type { ColumnGroupType, ColumnType } from "antd/es/table";
import { DT_FORMATS } from "./constants";

export type ExportColumn<T> = (ColumnType<T> | ColumnGroupType<T>) & {
  /** Cell text for the export when what the table renders is not what belongs in a sheet. */
  exportValue?: (record: T, index: number) => ReactNode;
  /** Keep this column out of the export (row action buttons and the like). */
  exportIgnore?: boolean;
};

/** Interchangeable with antd's `ColumnsType<T>` in both directions — the extras are optional. */
export type ExportColumns<T> = ExportColumn<T>[];

/**
 * Cells are read without rendering them, so a component's own markup is not walked — what
 * `<DocumentStatusTag status="ISSUED" />` shows is only knowable from its props. A component
 * that can appear in a table cell therefore declares its text as a `csvText` static (see
 * `ProjectLink`, `SiteLabel`, `DocumentStatusTag`, `StockBadge`, `DocLink`) and every export
 * of every table that renders it picks the text up. Without one, `exportValue` on the column
 * is the other way; a dev-build warning says so when neither is there.
 */
export type WithCsvText<P> = { csvText?: (props: P) => ReactNode };

// ============================================================
// ReactNode -> text
// ============================================================

/**
 * The text a rendered cell shows. Walks children the way the DOM would, so a fragment of
 * `<ProjectLink /> / {site.name}` reads back as "Harbour Phase 2 / Yard A". Components that
 * carry their label in a prop rather than in children (`Progress`, and every component with
 * a `csvText` static) are read from their props instead.
 */
export function nodeToText(node: unknown): string {
  return collect(node).replace(/\s+/g, " ").trim();
}

function collect(node: unknown): string {
  if (node == null || typeof node === "boolean") return "";
  if (typeof node === "string") return node;
  if (typeof node === "number" || typeof node === "bigint") return String(node);
  if (node instanceof Date) return dayjs(node).format(DT_FORMATS.dateTime);
  if (Array.isArray(node)) return node.map(collect).join("");
  if (isValidElement(node)) return elementText(node);
  if (typeof node === "object") {
    // antd lets a render return `{ children, props }` to set colSpan/rowSpan on the cell.
    const cell = node as { children?: ReactNode };
    return "children" in cell ? collect(cell.children) : "";
  }
  return "";
}

// Props that hold a component's visible label when it has no children to walk.
const LABEL_PROPS = ["label", "name", "text", "title", "value"];

function elementText(el: ReactNode): string {
  const { type, props } = el as { type: unknown; props?: Record<string, unknown> };
  const p = props ?? {};
  if (typeof type === "function") {
    const csvText = (type as WithCsvText<Record<string, unknown>>).csvText;
    if (typeof csvText === "function") return collect(csvText(p));
  }
  if (p.children != null) return collect(p.children);
  // antd Progress and friends: the number is the content, there are no children.
  if (typeof p.percent === "number") return `${p.percent}%`;
  for (const key of LABEL_PROPS) {
    const v = p[key];
    if (typeof v === "string" || typeof v === "number") return String(v);
  }
  if (import.meta.env.DEV && typeof type === "function") {
    const name = (type as { displayName?: string; name?: string }).displayName ?? (type as { name?: string }).name ?? "component";
    console.warn(`[csv] <${name}> exports as an empty cell — give it a csvText static, or the column an exportValue.`);
  }
  return "";
}

// ============================================================
// Columns -> cells
// ============================================================

/** Grouped headers flattened to their leaves; action columns (no header text) dropped. */
function exportableColumns<T>(columns: ExportColumns<T>): ExportColumn<T>[] {
  const out: ExportColumn<T>[] = [];
  for (const col of columns) {
    if (col.exportIgnore) continue;
    const children = (col as ColumnGroupType<T>).children as ExportColumns<T> | undefined;
    if (children?.length) {
      out.push(...exportableColumns(children));
      continue;
    }
    // A column with no header has nothing to call it in a spreadsheet — in this app that is
    // always a row-action button.
    if (!columnHeaderText(col)) continue;
    out.push(col);
  }
  return out;
}

/** A column's header as plain text — also what labels its field on the mobile card list. */
export function columnHeaderText<T>(col: ExportColumn<T>): string {
  const { title } = col;
  if (typeof title === "function") {
    // A title function is normally a sort-state renderer; it has to survive being called
    // outside the table, so anything it needs and doesn't get is not worth failing over.
    try {
      return nodeToText(title({ sortColumns: [], filters: {} }));
    } catch {
      return "";
    }
  }
  return nodeToText(title);
}

/** The raw field a column points at, following a `dataIndex` path into nested objects. */
export function columnValue<T>(record: T, dataIndex: ColumnType<T>["dataIndex"]): unknown {
  if (dataIndex == null) return undefined;
  const path = Array.isArray(dataIndex) ? dataIndex : [dataIndex];
  let cur: unknown = record;
  for (const key of path) {
    if (cur == null || typeof cur !== "object") return undefined;
    cur = (cur as Record<string, unknown>)[String(key)];
  }
  return cur;
}

function cellText<T>(col: ExportColumn<T>, record: T, index: number): string {
  if (col.exportValue) return nodeToText(col.exportValue(record, index));
  const raw = columnValue(record, (col as ColumnType<T>).dataIndex);
  if (typeof col.render === "function") return nodeToText(col.render(raw, record, index));
  if (typeof raw === "boolean") return raw ? "Yes" : "No";
  return nodeToText(raw);
}

// ============================================================
// CSV assembly
// ============================================================

// A cell opening with one of these is a formula to Excel and Sheets, not text, so a
// supplier named "=cmd" would run on someone's machine. Leading "-" is left alone: negative
// numbers are far more common in these exports than a formula starting with a minus.
const FORMULA_PREFIX = /^[=+@\t\r]/;

function csvCell(value: string): string {
  const s = FORMULA_PREFIX.test(value) ? `'${value}` : value;
  return /[",\r\n]|^\s|\s$/.test(s) ? `"${s.replaceAll('"', '""')}"` : s;
}

const csvRow = (cells: string[]) => cells.map(csvCell).join(",");

/** "Location: Central; Status: Approved" — the filter line of the header block. */
export function formatFilters(filters?: Record<string, unknown>): string {
  if (!filters) return "";
  return Object.entries(filters)
    .map(([k, v]) => [k, typeof v === "boolean" ? (v ? "Yes" : "No") : nodeToText(v)] as const)
    .filter(([, v]) => v !== "")
    .map(([k, v]) => `${prettifyKey(k)}: ${v}`)
    .join("; ");
}

function prettifyKey(key: string): string {
  const words = key
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/[_-]+/g, " ")
    .trim();
  return (words.charAt(0).toUpperCase() + words.slice(1)).replace(/\bId\b/g, "ID");
}

export interface BuildCsvOptions<T> {
  /** Report name — the first header line, and the base of the file name. */
  name: string;
  columns: ExportColumns<T>;
  rows: readonly T[];
  /** Shown on the Filters line; keys are prettified, empty values dropped. */
  filters?: Record<string, unknown>;
  /** Extra `label,value` header lines (a report's caveat, a period, a total). */
  extraHeaders?: [string, ReactNode][];
  generatedBy?: string;
  generatedAt?: dayjs.Dayjs;
}

/** The full file as text: header block, blank line, column headers, one line per row. */
export function buildTableCsv<T>({
  name,
  columns,
  rows,
  filters,
  extraHeaders = [],
  generatedBy,
  generatedAt = dayjs(),
}: BuildCsvOptions<T>): string {
  const cols = exportableColumns(columns);
  const lines: string[] = [
    csvRow(["Report", name]),
    csvRow(["Generated at", generatedAt.format(DT_FORMATS.dateTime)]),
  ];
  if (generatedBy) lines.push(csvRow(["Generated by", generatedBy]));
  const filterLine = formatFilters(filters);
  if (filterLine) lines.push(csvRow(["Filters", filterLine]));
  lines.push(csvRow(["Rows", String(rows.length)]));
  for (const [label, value] of extraHeaders) lines.push(csvRow([label, nodeToText(value)]));
  lines.push("");
  lines.push(csvRow(cols.map(columnHeaderText)));
  rows.forEach((row, i) => lines.push(csvRow(cols.map((c) => cellText(c, row, i)))));
  return lines.join("\r\n") + "\r\n";
}

export function csvFileName(name: string, at = dayjs()): string {
  const slug = name.trim().toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "") || "export";
  return `${slug}_${at.format("YYYY-MM-DD_HHmm")}.csv`;
}

export function downloadCsv(fileName: string, content: string) {
  // The BOM is what makes Excel read the file as UTF-8 rather than mangling "MVR", "—", "·".
  const blob = new Blob(["\uFEFF", content], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 0);
}

/** Build and download in one call — what `TableDownload` does on click. */
export function exportTableToCsv<T>(options: BuildCsvOptions<T> & { fileName?: string }) {
  const at = options.generatedAt ?? dayjs();
  downloadCsv(options.fileName ?? csvFileName(options.name, at), buildTableCsv({ ...options, generatedAt: at }));
}
